import React, { useEffect, useRef, useState } from 'react';
import { Alert, Card, Row, Col, Select, Segmented, Space, Empty, Table, Typography, Tooltip, Drawer, Input, InputNumber, Button } from 'antd';
import { EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';

const { Title, Text } = Typography;

// TODO: 联调时替换为登录角色（平台运营 / 设备制造商企业）
const ROLE_OPTIONS = [
    { label: '平台视角', value: 'platform' },
    { label: '企业视角', value: 'enterprise' },
];

// TODO: 联调时替换为企业远端搜索
const MOCK_ENTERPRISES = [
    { value: 'test-ent-mfg-a', label: '深圳铭远智能科技有限公司（test-ent-mfg-a）', factor: 1 },
    { value: 'test-ent-mfg-b', label: '东莞康林物联网有限公司（test-ent-mfg-b）', factor: 0.62 },
    { value: 'test-ent-mfg-c', label: '广州云联物联网有限公司（test-ent-mfg-c）', factor: 0.35 },
];

// TODO: 联调时替换为接口数据 —— 设备状态（月粒度，近12个月）
const MOCK_STATUS_MONTHLY = [
    { month: '2025-09', produced: 260, active: 250, bound: 210, totalDevice: 260 },
    { month: '2025-10', produced: 280, active: 270, bound: 230, totalDevice: 540 },
    { month: '2025-11', produced: 300, active: 290, bound: 250, totalDevice: 840 },
    { month: '2025-12', produced: 320, active: 310, bound: 270, totalDevice: 1160 },
    { month: '2026-01', produced: 340, active: 330, bound: 290, totalDevice: 1500 },
    { month: '2026-02', produced: 360, active: 350, bound: 300, totalDevice: 1860 },
    { month: '2026-03', produced: 300, active: 295, bound: 260, totalDevice: 2160 },
    { month: '2026-04', produced: 520, active: 500, bound: 430, totalDevice: 2680 },
    { month: '2026-05', produced: 800, active: 770, bound: 660, totalDevice: 3480 },
    { month: '2026-06', produced: 1200, active: 1150, bound: 980, totalDevice: 4680 },
    { month: '2026-07', produced: 1800, active: 1730, bound: 1500, totalDevice: 6480 },
    { month: '2026-08', produced: 2800, active: 2700, bound: 2350, totalDevice: 9280 },
];

// TODO: 联调时替换为接口数据 —— 设备状态（日粒度，近30天）
const MOCK_STATUS_DAILY = (() => {
    const arr = [];
    const base = new Date('2026-08-19');
    const prodSeed = [32, 28, 35, 40, 30, 45, 38, 50, 42, 36, 55, 48, 40, 52, 45, 60, 50, 42, 58, 65, 54, 46, 62, 70, 58, 50, 66, 75, 62, 55];
    let cum = 0;
    for (let i = 29; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const prod = prodSeed[29 - i];
        cum += prod;
        arr.push({
            date: dateStr,
            produced: prod,
            active: Math.round(prod * 0.96),
            bound: Math.round(prod * 0.82),
            totalDevice: cum,
        });
    }
    return arr;
})();

// 根据时间范围返回设备状态趋势数据（日粒度：近7天/近30天/本月；月粒度：近3月/6月/一年）
const getStatusTrendData = (range) => {
    if (range === '7d' || range === '30d' || range === 'month') {
        let daily = MOCK_STATUS_DAILY;
        if (range === '7d') daily = daily.slice(-7);
        if (range === 'month') daily = daily.filter((d) => d.date.startsWith('2026-08'));
        return {
            labels: daily.map((d) => d.date.slice(5)),
            produced: daily.map((d) => d.produced),
            active: daily.map((d) => d.active),
            bound: daily.map((d) => d.bound),
            totalDevice: daily.map((d) => d.totalDevice),
            latestTotal: MOCK_STATUS_DAILY[MOCK_STATUS_DAILY.length - 1].totalDevice,
        };
    }
    const months = { '3m': 3, '6m': 6, '1y': 12 }[range];
    const monthly = MOCK_STATUS_MONTHLY.slice(-months);
    return {
        labels: monthly.map((m) => m.month.slice(2)),
        produced: monthly.map((m) => m.produced),
        active: monthly.map((m) => m.active),
        bound: monthly.map((m) => m.bound),
        totalDevice: monthly.map((m) => m.totalDevice),
        latestTotal: MOCK_STATUS_MONTHLY[MOCK_STATUS_MONTHLY.length - 1].totalDevice,
    };
};

// TODO: 联调时替换为接口数据 —— 设备营收（月粒度，近12个月）
const MOCK_REVENUE_MONTHLY = [
    { month: '2025-09', orderCnt: 180, revenueCny: 18000, revenueUsd: 2200 },
    { month: '2025-10', orderCnt: 200, revenueCny: 21000, revenueUsd: 2500 },
    { month: '2025-11', orderCnt: 210, revenueCny: 24000, revenueUsd: 2800 },
    { month: '2025-12', orderCnt: 230, revenueCny: 26000, revenueUsd: 3000 },
    { month: '2026-01', orderCnt: 250, revenueCny: 28000, revenueUsd: 3200 },
    { month: '2026-02', orderCnt: 240, revenueCny: 30000, revenueUsd: 3400 },
    { month: '2026-03', orderCnt: 260, revenueCny: 28000, revenueUsd: 3200 },
    { month: '2026-04', orderCnt: 290, revenueCny: 36000, revenueUsd: 4300 },
    { month: '2026-05', orderCnt: 320, revenueCny: 45000, revenueUsd: 5200 },
    { month: '2026-06', orderCnt: 360, revenueCny: 52000, revenueUsd: 6100 },
    { month: '2026-07', orderCnt: 400, revenueCny: 60000, revenueUsd: 6800 },
    { month: '2026-08', orderCnt: 440, revenueCny: 64000, revenueUsd: 7300 },
];

// TODO: 联调时替换为接口数据 —— 设备营收（日粒度，近30天）
const MOCK_REVENUE_DAILY = (() => {
    const arr = [];
    const base = new Date('2026-08-19');
    const orderSeed = [9, 7, 8, 10, 8, 11, 9, 12, 10, 8, 13, 11, 9, 12, 10, 14, 11, 9, 13, 15, 12, 10, 14, 16, 13, 11, 15, 17, 14, 12];
    const cnySeed = [2200, 1800, 2100, 2600, 1900, 2800, 2300, 3100, 2500, 2000, 3300, 2700, 2400, 3000, 2600, 3500, 2900, 2300, 3400, 3800, 3100, 2500, 3600, 4000, 3300, 2700, 3700, 4100, 3500, 3200];
    for (let i = 29; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const cny = cnySeed[29 - i];
        arr.push({ date: dateStr, orderCnt: orderSeed[29 - i], revenueCny: cny, revenueUsd: Math.round(cny / 8) });
    }
    return arr;
})();

const timeRangeOptions = [
    { value: '7d', label: '近7天' },
    { value: '30d', label: '近30天' },
    { value: 'month', label: '本月' },
    { value: '3m', label: '近3个月' },
    { value: '6m', label: '近6个月' },
    { value: '1y', label: '近一年' },
];

// 根据时间范围返回营收趋势数据（日粒度：近7天/近30天/本月；月粒度：近3月/6月/一年）
const getRevenueTrendData = (range) => {
    if (range === '7d' || range === '30d' || range === 'month') {
        let daily = MOCK_REVENUE_DAILY;
        if (range === '7d') daily = daily.slice(-7);
        if (range === 'month') daily = daily.filter((d) => d.date.startsWith('2026-08'));
        return {
            labels: daily.map((d) => d.date.slice(5)),
            orderCnt: daily.map((d) => d.orderCnt),
            cny: daily.map((d) => d.revenueCny),
            usd: daily.map((d) => d.revenueUsd),
        };
    }
    const months = { '3m': 3, '6m': 6, '1y': 12 }[range];
    const monthly = MOCK_REVENUE_MONTHLY.slice(-months);
    return {
        labels: monthly.map((m) => m.month.slice(2)),
        orderCnt: monthly.map((m) => m.orderCnt),
        cny: monthly.map((m) => m.revenueCny),
        usd: monthly.map((m) => m.revenueUsd),
    };
};

// TODO: 联调时替换为接口数据 —— 明星产品（8 个，各维度排名错开）
const MOCK_TOP_PRODUCTS = [
    { key: '1', name: 'test-自研高清摄像机', id: 'test-prod-001', devType: 'self', devCycle: 13, avgProduceCycle: 4, giftAuth: 300, authCount: 1000, authAmount: 80000, producedDevice: 1200, activeDevice: 1100, boundDevice: 900, revenueCny: 48000, revenueUsd: 6000 },
    { key: '2', name: 'test-户外枪机', id: 'test-prod-006', devType: 'self', devCycle: 10, avgProduceCycle: 5, giftAuth: 200, authCount: 800, authAmount: 96000, producedDevice: 900, activeDevice: 880, boundDevice: 850, revenueCny: 68000, revenueUsd: 9500 },
    { key: '3', name: 'test-4G低功耗摄像机', id: 'test-prod-003', devType: 'self', devCycle: 11, avgProduceCycle: 5, giftAuth: 100, authCount: 400, authAmount: 51600, producedDevice: 320, activeDevice: 320, boundDevice: 310, revenueCny: 45000, revenueUsd: 6200 },
    { key: '4', name: 'test-智能门铃', id: 'test-prod-004', devType: 'self', devCycle: 13, avgProduceCycle: 5, giftAuth: 100, authCount: 400, authAmount: 26000, producedDevice: 400, activeDevice: 400, boundDevice: 380, revenueCny: 12000, revenueUsd: 1500 },
    { key: '5', name: 'test-室内云台', id: 'test-prod-007', devType: 'self', devCycle: 9, avgProduceCycle: 4, giftAuth: 150, authCount: 450, authAmount: 40500, producedDevice: 500, activeDevice: 500, boundDevice: 490, revenueCny: 25000, revenueUsd: 3300 },
    { key: '6', name: 'test-NVR录像机', id: 'test-prod-008', devType: 'self', devCycle: 14, avgProduceCycle: 6, giftAuth: 200, authCount: 700, authAmount: 105000, producedDevice: 800, activeDevice: 750, boundDevice: 720, revenueCny: 30000, revenueUsd: 4000 },
    { key: '7', name: 'test-派生云台摄像机A款', id: 'test-prod-002', devType: 'derived', devCycle: 6, avgProduceCycle: 5, giftAuth: 100, authCount: 800, authAmount: 64000, producedDevice: 650, activeDevice: 640, boundDevice: 600, revenueCny: 27000, revenueUsd: 3500 },
    { key: '8', name: 'test-派生门铃标准版', id: 'test-prod-005', devType: 'derived', devCycle: 6, avgProduceCycle: 4, giftAuth: 50, authCount: 200, authAmount: 25800, producedDevice: 180, activeDevice: 180, boundDevice: 90, revenueCny: 5000, revenueUsd: 700 },
    { key: '9', name: 'test-智能门锁', id: 'test-prod-009', devType: 'self', devCycle: 12, avgProduceCycle: 5, giftAuth: 120, authCount: 500, authAmount: 45000, producedDevice: 450, activeDevice: 430, boundDevice: 360, revenueCny: 20000, revenueUsd: 2500 },
    { key: '10', name: 'test-全景摄像机', id: 'test-prod-010', devType: 'self', devCycle: 15, avgProduceCycle: 6, giftAuth: 250, authCount: 900, authAmount: 126000, producedDevice: 850, activeDevice: 820, boundDevice: 760, revenueCny: 55000, revenueUsd: 7000 },
    { key: '11', name: 'test-婴儿监视器', id: 'test-prod-011', devType: 'self', devCycle: 10, avgProduceCycle: 4, giftAuth: 80, authCount: 300, authAmount: 24000, producedDevice: 280, activeDevice: 270, boundDevice: 230, revenueCny: 15000, revenueUsd: 1800 },
    { key: '12', name: 'test-门磁传感器', id: 'test-prod-012', devType: 'self', devCycle: 8, avgProduceCycle: 3, giftAuth: 60, authCount: 200, authAmount: 12000, producedDevice: 180, activeDevice: 175, boundDevice: 150, revenueCny: 6000, revenueUsd: 750 },
    { key: '13', name: 'test-烟雾报警器', id: 'test-prod-013', devType: 'self', devCycle: 9, avgProduceCycle: 4, giftAuth: 90, authCount: 350, authAmount: 28000, producedDevice: 320, activeDevice: 310, boundDevice: 270, revenueCny: 11000, revenueUsd: 1400 },
    { key: '14', name: 'test-温湿度传感器', id: 'test-prod-014', devType: 'self', devCycle: 7, avgProduceCycle: 3, giftAuth: 50, authCount: 150, authAmount: 9000, producedDevice: 140, activeDevice: 135, boundDevice: 110, revenueCny: 4500, revenueUsd: 560 },
    { key: '15', name: 'test-智能插座', id: 'test-prod-015', devType: 'self', devCycle: 9, avgProduceCycle: 4, giftAuth: 100, authCount: 400, authAmount: 32000, producedDevice: 380, activeDevice: 360, boundDevice: 320, revenueCny: 16000, revenueUsd: 2000 },
    { key: '16', name: 'test-智能灯泡', id: 'test-prod-016', devType: 'self', devCycle: 6, avgProduceCycle: 3, giftAuth: 70, authCount: 250, authAmount: 15000, producedDevice: 240, activeDevice: 230, boundDevice: 200, revenueCny: 8000, revenueUsd: 1000 },
    { key: '17', name: 'test-门磁门铃', id: 'test-prod-017', devType: 'self', devCycle: 11, avgProduceCycle: 5, giftAuth: 110, authCount: 450, authAmount: 36000, producedDevice: 420, activeDevice: 400, boundDevice: 350, revenueCny: 18000, revenueUsd: 2200 },
    { key: '18', name: 'test-户外太阳能摄像机', id: 'test-prod-018', devType: 'self', devCycle: 14, avgProduceCycle: 6, giftAuth: 300, authCount: 1100, authAmount: 154000, producedDevice: 1000, activeDevice: 950, boundDevice: 880, revenueCny: 62000, revenueUsd: 7800 },
    { key: '19', name: 'test-室内半球机', id: 'test-prod-019', devType: 'self', devCycle: 12, avgProduceCycle: 5, giftAuth: 180, authCount: 650, authAmount: 58500, producedDevice: 600, activeDevice: 580, boundDevice: 520, revenueCny: 33000, revenueUsd: 4100 },
    { key: '20', name: 'test-枪球联动摄像机', id: 'test-prod-020', devType: 'self', devCycle: 16, avgProduceCycle: 7, giftAuth: 400, authCount: 1500, authAmount: 210000, producedDevice: 1400, activeDevice: 1350, boundDevice: 1250, revenueCny: 75000, revenueUsd: 9500 },
];

const RANK_BADGE = {
    1: { text: 'Top1', color: '#f59e0b' },
    2: { text: 'Top2', color: '#9ca3af' },
    3: { text: 'Top3', color: '#d97706' },
    4: { text: 'Top4', color: '#1890ff' },
    5: { text: 'Top5', color: '#722ed1' },
};

// 激活后月龄营收权重（13 桶：1~12 月 + 12 月后至今），营收随月龄衰减
const MONTH_WEIGHTS = [12, 11, 10, 10, 9, 8, 8, 7, 6, 5, 4, 3, 7];
const MONTH_LABELS = ['激活后1个月', '激活后2个月', '激活后3个月', '激活后4个月', '激活后5个月', '激活后6个月', '激活后7个月', '激活后8个月', '激活后9个月', '激活后10个月', '激活后11个月', '激活后12个月', '12个月后至今'];
const MONTH_SHORT = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月', '12月+'];

// 按激活后月龄把总营收（折¥）分配到 13 个桶，最后一项补齐误差保证求和等于总营收
const buildRevenueByMonth = (totalCny, seed) => {
    const weights = MONTH_WEIGHTS.map((w, idx) => w + ((idx + seed) % 3) - 1);
    const wSum = weights.reduce((s, w) => s + w, 0);
    let acc = 0;
    return weights.map((w, idx) => {
        if (idx === weights.length - 1) {
            const v = Math.round(totalCny - acc);
            acc = totalCny;
            return v;
        }
        const v = Math.round((totalCny * w) / wSum);
        acc += v;
        return v;
    });
};

// 表头不换行
const tableHeaderNowrap = {
    header: {
        cell: (props) => <th {...props} style={{ ...props.style, whiteSpace: 'nowrap' }} />,
    },
};

// TODO: 联调时替换为接口数据 —— 批次由产品数据拆分，保证批次汇总可回到产品
const BATCH_COUNT = 3;

const buildBatchesForProduct = (product) => {
    if (!product) return [];
    const splitCount = (total, i) => {
        const base = Math.floor(total / BATCH_COUNT);
        return i === BATCH_COUNT - 1 ? total - base * (BATCH_COUNT - 1) : base;
    };
    const splitMoney = (total, i) => {
        const base = Number((total / BATCH_COUNT).toFixed(2));
        return i === BATCH_COUNT - 1 ? Number((total - base * (BATCH_COUNT - 1)).toFixed(2)) : base;
    };
    return Array.from({ length: BATCH_COUNT }, (_, i) => ({
        key: `${product.key}-batch-${i + 1}`,
        batchName: `${product.name}-批次${i + 1}`,
        batchId: `${product.id}-WO${String(i + 1).padStart(2, '0')}`,
        avgProduceCycle: Math.max(1, product.avgProduceCycle + (i - 1)),
        saleCycle: 10 + i * 3,
        purchaseCost: splitMoney(product.authAmount, i),
        producedDevice: splitCount(product.producedDevice, i),
        activeDevice: splitCount(product.activeDevice, i),
        boundDevice: splitCount(product.boundDevice, i),
        revenueCny: splitMoney(product.revenueCny, i),
        revenueUsd: splitMoney(product.revenueUsd, i),
    }));
};

const DeviceDashboard = () => {
    // mock 角色视角：真实业务由登录角色决定
    const [mockRole, setMockRole] = useState('platform');
    // 平台视角默认不选企业，必须选择后展示数据
    const [entId, setEntId] = useState('');
    const isPlatform = mockRole === 'platform';
    const selectedEnt = MOCK_ENTERPRISES.find((e) => e.value === entId);
    const entFactor = isPlatform ? (selectedEnt?.factor ?? 0) : 1;
    const hasData = !isPlatform || Boolean(entId);

    const [timeRange, setTimeRange] = useState('7d');
    const [batchVisible, setBatchVisible] = useState(false);
    const [batchRecord, setBatchRecord] = useState(null);
    const [batchKeyword, setBatchKeyword] = useState('');
    const [dayRate, setDayRate] = useState(6.50);
    const [dayRateEditing, setDayRateEditing] = useState(false);
    const [dayRateInput, setDayRateInput] = useState(6.50);
    const [usdRate, setUsdRate] = useState(7.30);
    const [usdRateEditing, setUsdRateEditing] = useState(false);
    const [usdRateInput, setUsdRateInput] = useState(7.30);
    const [batchDayRateEditing, setBatchDayRateEditing] = useState(false);
    const [batchDayRateInput, setBatchDayRateInput] = useState(6.50);
    const [batchUsdRateEditing, setBatchUsdRateEditing] = useState(false);
    const [batchUsdRateInput, setBatchUsdRateInput] = useState(7.30);

    const handleSaveDayRate = () => {
        setDayRate(dayRateInput);
        setDayRateEditing(false);
    };
    const handleSaveUsdRate = () => {
        setUsdRate(usdRateInput);
        setUsdRateEditing(false);
    };
    const handleSaveBatchDayRate = () => {
        setDayRate(batchDayRateInput);
        setBatchDayRateEditing(false);
    };
    const handleSaveBatchUsdRate = () => {
        setUsdRate(batchUsdRateInput);
        setBatchUsdRateEditing(false);
    };

    const handleRoleChange = (role) => {
        setMockRole(role);
        setEntId('');
        setBatchVisible(false);
        setBatchRecord(null);
    };

    const handleEntChange = (value) => {
        setEntId(value);
        setBatchVisible(false);
        setBatchRecord(null);
    };

    // mock：按所选企业缩放经营数据，便于演示不同企业的数据差异
    const scaleCount = (v) => Math.round(v * entFactor);
    const scaleMoney = (v) => Number((v * entFactor).toFixed(2));

    const scaledProducts = MOCK_TOP_PRODUCTS.map((p) => ({
        ...p,
        giftAuth: scaleCount(p.giftAuth),
        authCount: scaleCount(p.authCount),
        authAmount: scaleMoney(p.authAmount),
        producedDevice: scaleCount(p.producedDevice),
        activeDevice: scaleCount(p.activeDevice),
        boundDevice: scaleCount(p.boundDevice),
        revenueCny: scaleMoney(p.revenueCny),
        revenueUsd: scaleMoney(p.revenueUsd),
    }));

    // 核心指标由产品明细汇总，保证「累计指标 = 各产品之和」
    const sumBy = (key) => scaledProducts.reduce((sum, p) => sum + p[key], 0);
    const avgBy = (key) => (scaledProducts.length ? sumBy(key) / scaledProducts.length : 0);

    const kpiData = {
        avgDevCycle: Number(avgBy('devCycle').toFixed(1)),
        avgProduceCycle: Number(avgBy('avgProduceCycle').toFixed(1)),
        giftAuthTotal: sumBy('giftAuth'),
        authCount: sumBy('authCount'),
        authAmount: Number(sumBy('authAmount').toFixed(2)),
        producedDeviceCount: sumBy('producedDevice'),
        activeDeviceTotal: sumBy('activeDevice'),
        boundDeviceCount: sumBy('boundDevice'),
        revenueCny: Number(sumBy('revenueCny').toFixed(2)),
        revenueUsd: Number(sumBy('revenueUsd').toFixed(2)),
    };
    const producedTotal = kpiData.producedDeviceCount;

    const trend1Ref = useRef(null);
    const trend2Ref = useRef(null);
    const trend1Instance = useRef(null);
    const trend2Instance = useRef(null);

    // 图表1：设备状态趋势（新增生产设备数 + 存量设备数堆叠 + 新增激活/绑定数量，依赖时间范围）
    useEffect(() => {
        const raw = getStatusTrendData(timeRange);
        const labels = raw.labels;
        const produced = raw.produced.map((v) => Math.round(v * entFactor));
        const active = raw.active.map((v) => Math.round(v * entFactor));
        const bound = raw.bound.map((v) => Math.round(v * entFactor));
        const totalDevice = raw.totalDevice.map((v) => Math.round(v * entFactor) + Math.max(0, producedTotal - Math.round(raw.latestTotal * entFactor)));
        const stock = totalDevice.map((t, i) => Math.max(0, t - produced[i]));
        if (!trend1Ref.current) return;
        if (!trend1Instance.current) {
            // 首次初始化
            trend1Instance.current = echarts.init(trend1Ref.current);
            trend1Instance.current.setOption({
                legend: { data: ['新增生产设备数', '存量设备数', '新增激活数量', '新增绑定数量'], top: 10, selectedMode: true },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
                grid: { top: 60, left: 60, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '数量', min: 0, minInterval: 1, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    { name: '新增生产设备数', type: 'bar', data: produced, stack: 'total', barWidth: 24, itemStyle: { color: '#5470c6' } },
                    { name: '存量设备数', type: 'bar', data: stock, stack: 'total', barWidth: 24, itemStyle: { color: '#91cc75' } },
                    { name: '新增激活数量', type: 'line', data: active, smooth: true, lineStyle: { color: '#fac858', width: 2.5 }, itemStyle: { color: '#fac858' }, symbol: 'circle', symbolSize: 6 },
                    { name: '新增绑定数量', type: 'line', data: bound, smooth: true, lineStyle: { color: '#ee6666', width: 2.5 }, itemStyle: { color: '#ee6666' }, symbol: 'circle', symbolSize: 6 },
                ],
            });
        } else {
            // 切换时间范围时合并更新，保留图例勾选状态（不 dispose）
            trend1Instance.current.setOption({
                xAxis: { data: labels },
                series: [
                    { name: '新增生产设备数', data: produced },
                    { name: '存量设备数', data: stock },
                    { name: '新增激活数量', data: active },
                    { name: '新增绑定数量', data: bound },
                ],
            });
        }
    }, [timeRange, entFactor, hasData, producedTotal]);

    // 图表2：设备营收趋势（新增增值订单数 + 新增营收金额 ¥ / $，依赖时间范围筛选）
    useEffect(() => {
        const raw = getRevenueTrendData(timeRange);
        const labels = raw.labels;
        const orderCnt = raw.orderCnt.map((v) => Math.round(v * entFactor));
        const cny = raw.cny.map((v) => Number((v * entFactor).toFixed(2)));
        const usd = raw.usd.map((v) => Number((v * entFactor).toFixed(2)));
        if (!trend2Ref.current) return;
        if (!trend2Instance.current) {
            // 首次初始化
            trend2Instance.current = echarts.init(trend2Ref.current);
            trend2Instance.current.setOption({
                legend: { data: ['新增增值订单数', '新增营收金额(¥)', '新增营收金额($)'], top: 10, selectedMode: true },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
                grid: { top: 60, left: 70, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '订单数 / 金额', min: 0, minInterval: 1, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    { name: '新增增值订单数', type: 'bar', data: orderCnt, barWidth: 24, itemStyle: { color: '#fac858' } },
                    { name: '新增营收金额(¥)', type: 'line', data: cny, smooth: true, lineStyle: { color: '#ee6666', width: 2.5 }, itemStyle: { color: '#ee6666' }, symbol: 'circle', symbolSize: 6 },
                    { name: '新增营收金额($)', type: 'line', data: usd, smooth: true, lineStyle: { color: '#722ed1', width: 2.5 }, itemStyle: { color: '#722ed1' }, symbol: 'circle', symbolSize: 6 },
                ],
            });
        } else {
            // 切换时间范围时合并更新，保留图例勾选状态（不 dispose）
            trend2Instance.current.setOption({
                xAxis: { data: labels },
                series: [
                    { name: '新增增值订单数', data: orderCnt },
                    { name: '新增营收金额(¥)', data: cny },
                    { name: '新增营收金额($)', data: usd },
                ],
            });
        }
    }, [timeRange, entFactor, hasData]);

    // 数据为空（平台未选企业）时销毁图表实例，避免重新选择企业后复用已卸载的 DOM
    useEffect(() => {
        if (hasData) return;
        trend1Instance.current?.dispose();
        trend2Instance.current?.dispose();
        trend1Instance.current = null;
        trend2Instance.current = null;
    }, [hasData]);

    useEffect(() => {
        const handleResize = () => {
            trend1Instance.current?.resize();
            trend2Instance.current?.resize();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const totalProfit = scaledProducts.reduce((sum, r) => sum + (r.revenueCny + r.revenueUsd * usdRate - (r.devCycle + r.avgProduceCycle) * dayRate - r.authAmount), 0);

    const kpiCards = [
        { title: '平均开发周期', value: kpiData.avgDevCycle, suffix: '天', color: '#2f54eb', tip: '产品创建 → 发布审核通过的平均周期' },
        { title: '平均生产周期', value: kpiData.avgProduceCycle, suffix: '天', color: '#faad14', tip: '工单创建 → 生产完成的平均周期' },
        { title: '累计赠送授权', value: kpiData.giftAuthTotal, suffix: '个', color: '#13c2c2', tip: '平台赠送的授权累计' },
        { title: '累计授权采购', value: kpiData.authCount, suffix: '个', color: '#fa8c16', tip: '企业累计采购的生产授权数' },
        { title: '累计采购成本', value: kpiData.authAmount, money: true, color: '#eb2f96', tip: '企业采购授权的累计金额（仅企业采购来源）' },
        { title: '累计生产设备', value: kpiData.producedDeviceCount, suffix: '台', color: '#52c41a', tip: '生产工单生成的 License 总数' },
        { title: '累计激活设备', value: kpiData.activeDeviceTotal, suffix: '台', color: '#722ed1', tip: '已激活上云的设备数' },
        { title: '累计绑定设备', value: kpiData.boundDeviceCount, suffix: '台', color: '#a0d911', tip: '用户已绑定使用的设备数' },
        { title: '设备营收', cny: kpiData.revenueCny, usd: kpiData.revenueUsd, color: '#ff4d4f', tip: '设备在 App 侧购买增值服务的营收', multiCurrency: true },
        { title: '总计盈亏', value: totalProfit, profit: true, tip: '设备营收扣除时间成本与License成本后的总盈亏' },
    ];

    const revenueMonthColumns = [{
        title: '激活N个月后的营收统计',
        key: 'revenue-by-month',
        children: MONTH_SHORT.map((short, idx) => ({
            title: <Tooltip title={MONTH_LABELS[idx]}><span>{short}</span></Tooltip>,
            key: `rev-m${idx}`,
            width: 100,
            align: 'right',
            sorter: (a, b) => (a.revenueByMonth?.[idx]?.cny || 0) - (b.revenueByMonth?.[idx]?.cny || 0),
            render: (_, r) => {
                const cell = r.revenueByMonth?.[idx] || { cny: 0, usd: 0 };
                return (
                    <div>
                        <div style={{ whiteSpace: 'nowrap' }}>¥{cell.cny.toLocaleString('zh-CN')}</div>
                        <div style={{ whiteSpace: 'nowrap' }}>${cell.usd.toLocaleString('zh-CN')}</div>
                    </div>
                );
            },
        })),
    }];

    const topColumns = [
        {
            title: '排名', dataIndex: 'rank', width: 80, align: 'center', sorter: (a, b) => a.rank - b.rank,
            render: (v) => RANK_BADGE[v]
                ? <span style={{ display: 'inline-block', minWidth: 44, padding: '2px 8px', borderRadius: 10, background: RANK_BADGE[v].color, color: '#fff', fontWeight: 600, fontSize: 12 }}>{RANK_BADGE[v].text}</span>
                : <Text strong style={{ color: '#999' }}>{v}</Text>,
        },
        {
            title: '产品', key: 'product', width: 240,
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }} onClick={() => { setBatchRecord(r); setBatchVisible(true); setBatchKeyword(''); setBatchDayRateEditing(false); setBatchUsdRateEditing(false); }}>{r.name}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text>
            </div>),
        },
        { title: '开发周期', dataIndex: 'devCycle', width: 100, align: 'right', sorter: (a, b) => a.devCycle - b.devCycle, render: (v) => <Text strong style={{ color: '#fa8c16' }}>{v}天</Text> },
        { title: '平均生产周期', dataIndex: 'avgProduceCycle', width: 120, align: 'right', sorter: (a, b) => a.avgProduceCycle - b.avgProduceCycle, render: (v) => <Text strong>{v}天</Text> },
        { title: '消耗License', key: 'license', width: 110, align: 'right', sorter: (a, b) => (a.giftAuth + a.authCount) - (b.giftAuth + b.authCount), render: (_, r) => <Text strong>{(r.giftAuth + r.authCount).toLocaleString()}</Text> },
        { title: 'License成本', dataIndex: 'authAmount', width: 130, align: 'right', sorter: (a, b) => a.authAmount - b.authAmount, render: (v) => <Text strong>¥{v.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text> },
        { title: '生产设备', dataIndex: 'producedDevice', width: 100, align: 'right', sorter: (a, b) => a.producedDevice - b.producedDevice, render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        { title: '激活设备', dataIndex: 'activeDevice', width: 100, align: 'right', sorter: (a, b) => a.activeDevice - b.activeDevice, render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        { title: '绑定设备', dataIndex: 'boundDevice', width: 100, align: 'right', defaultSortOrder: 'descend', sorter: (a, b) => a.boundDevice - b.boundDevice, render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        ...revenueMonthColumns,
        {
            title: '总营收', key: 'revenue-total', width: 150, align: 'right',
            sorter: (a, b) => (a.revenueCny + a.revenueUsd * usdRate) - (b.revenueCny + b.revenueUsd * usdRate),
            render: (_, r) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>¥{r.revenueCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                    <div style={{ whiteSpace: 'nowrap' }}>${r.revenueUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                </div>
            ),
        },
        {
            title: '盈亏情况', key: 'profit', width: 130, align: 'right',
            sorter: (a, b) => {
                const pa = a.revenueCny + a.revenueUsd * usdRate - (a.devCycle + a.avgProduceCycle) * dayRate - a.authAmount;
                const pb = b.revenueCny + b.revenueUsd * usdRate - (b.devCycle + b.avgProduceCycle) * dayRate - b.authAmount;
                return pa - pb;
            },
            render: (_, r) => {
                const profit = r.revenueCny + r.revenueUsd * usdRate - (r.devCycle + r.avgProduceCycle) * dayRate - r.authAmount;
                const sign = profit >= 0 ? '+' : '-';
                return <Text strong style={{ color: profit >= 0 ? '#52c41a' : '#ff4d4f' }}>{sign}¥{Math.abs(profit).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>;
            },
        },
    ];

    const batchColumns = [
        {
            title: '批次（工单）', key: 'batch', width: 220,
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500 }}>{r.batchName}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.batchId}</Text>
            </div>),
        },
        { title: '生产周期', dataIndex: 'avgProduceCycle', width: 100, align: 'right', sorter: (a, b) => a.avgProduceCycle - b.avgProduceCycle, render: (v) => <Text strong>{v}天</Text> },
        { title: '销售周期', dataIndex: 'saleCycle', width: 90, align: 'right', sorter: (a, b) => a.saleCycle - b.saleCycle, render: (v) => <Text strong>{v}天</Text> },
        { title: '采购成本', dataIndex: 'purchaseCost', width: 120, align: 'right', sorter: (a, b) => a.purchaseCost - b.purchaseCost, render: (v) => <Text strong>¥{v.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text> },
        { title: '生产设备', dataIndex: 'producedDevice', width: 90, align: 'right', sorter: (a, b) => a.producedDevice - b.producedDevice, render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        { title: '激活设备', dataIndex: 'activeDevice', width: 90, align: 'right', sorter: (a, b) => a.activeDevice - b.activeDevice, render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        { title: '绑定设备', dataIndex: 'boundDevice', width: 90, align: 'right', sorter: (a, b) => a.boundDevice - b.boundDevice, render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        ...revenueMonthColumns,
        {
            title: '批次营收', key: 'revenue', width: 130, align: 'right', sorter: (a, b) => a.revenueCny - b.revenueCny,
            render: (_, r) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>¥{r.revenueCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                    <div style={{ whiteSpace: 'nowrap' }}>${r.revenueUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                </div>
            ),
        },
        {
            title: '批次盈亏', key: 'profit', width: 130, align: 'right',
            sorter: (a, b) => {
                const pa = a.revenueCny + a.revenueUsd * usdRate - (a.avgProduceCycle + a.saleCycle) * dayRate - a.purchaseCost;
                const pb = b.revenueCny + b.revenueUsd * usdRate - (b.avgProduceCycle + b.saleCycle) * dayRate - b.purchaseCost;
                return pa - pb;
            },
            render: (_, r) => {
                const profit = r.revenueCny + r.revenueUsd * usdRate - (r.avgProduceCycle + r.saleCycle) * dayRate - r.purchaseCost;
                const sign = profit >= 0 ? '+' : '-';
                return <Text strong style={{ color: profit >= 0 ? '#52c41a' : '#ff4d4f' }}>{sign}¥{Math.abs(profit).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>;
            },
        },
    ];

    const filteredBatches = buildBatchesForProduct(batchRecord)
        .filter((b) => !batchKeyword || b.batchName.includes(batchKeyword) || b.batchId.includes(batchKeyword))
        .map((b) => {
            const seed = parseInt(b.key.split('-').pop(), 10) || 0;
            const cnyArr = buildRevenueByMonth(b.revenueCny, seed);
            const usdArr = buildRevenueByMonth(b.revenueUsd, seed + 5);
            return { ...b, revenueByMonth: cnyArr.map((cny, idx) => ({ cny, usd: usdArr[idx] })) };
        });

    const productList = [...scaledProducts]
        .sort((a, b) => b.boundDevice - a.boundDevice)
        .map((r, i) => {
            const seed = parseInt(r.key, 10) || 0;
            const cnyArr = buildRevenueByMonth(r.revenueCny, seed);
            const usdArr = buildRevenueByMonth(r.revenueUsd, seed + 5);
            return { ...r, rank: i + 1, revenueByMonth: cnyArr.map((cny, idx) => ({ cny, usd: usdArr[idx] })) };
        });

    const alertMessage = isPlatform
        ? '本看板展示所选企业从产品开发、授权采购、生产，到设备激活与营收的全链路经营数据；请先选择企业后查看。'
        : '本看板展示贵司从产品开发、授权采购、生产，到设备激活与营收的全链路经营数据，统计范围仅本企业。';

    // mock 角色视角切换：真实业务由登录角色决定
    const roleSwitch = (
        <Space size={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>mock 角色视角切换</Text>
            <Segmented value={mockRole} onChange={handleRoleChange} options={ROLE_OPTIONS} />
        </Space>
    );

    // 平台视角：必须先选择企业；企业视角不展示筛选
    const enterpriseFilter = isPlatform ? (
        <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Space size={12} wrap>
                <span style={{ color: '#606266' }}>查看企业</span>
                <Select
                    value={entId || undefined}
                    onChange={handleEntChange}
                    placeholder="请选择企业"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={MOCK_ENTERPRISES}
                    style={{ width: 320 }}
                />
                {!entId && <Text type="secondary" style={{ fontSize: 12 }}>选择企业后查看数据</Text>}
            </Space>
        </Card>
    ) : null;

    if (!hasData) {
        return (
            <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>设备盈亏分析</Title>
                    {roleSwitch}
                </div>
                <Alert
                    type="warning"
                    showIcon
                    icon={<EyeOutlined />}
                    message={alertMessage}
                    style={{ marginBottom: 16, borderRadius: 8 }}
                />
                {enterpriseFilter}
                <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                    <Empty description="请选择企业后查看数据" />
                </Card>
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>设备盈亏分析</Title>
                {roleSwitch}
            </div>
            <Alert
                type="warning"
                showIcon
                icon={<EyeOutlined />}
                message={alertMessage}
                style={{ marginBottom: 16, borderRadius: 8 }}
            />
            {enterpriseFilter}

            {/* ═══════════ 核心指标 ═══════════ */}
            <Card
                title="核心指标"
                extra={(
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                        {dayRateEditing ? (
                            <>
                                <span style={{ color: '#606266', fontSize: 13 }}>1天按¥</span>
                                <InputNumber value={dayRateInput} onChange={setDayRateInput} min={0} precision={2} size="small" style={{ width: 80 }} />
                                <span style={{ color: '#606266', fontSize: 13 }}>折算</span>
                                <Button type="link" size="small" style={{ padding: 0 }} onClick={handleSaveDayRate}>保存</Button>
                            </>
                        ) : (
                            <>
                                <span style={{ color: '#606266', fontSize: 13 }}>1天按¥{dayRate.toFixed(2)}元折算</span>
                                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setDayRateEditing(true)}>修改</Button>
                            </>
                        )}
                        <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                        {usdRateEditing ? (
                            <>
                                <span style={{ color: '#606266', fontSize: 13 }}>$1.00按¥</span>
                                <InputNumber value={usdRateInput} onChange={setUsdRateInput} min={0} precision={2} size="small" style={{ width: 80 }} />
                                <span style={{ color: '#606266', fontSize: 13 }}>折算</span>
                                <Button type="link" size="small" style={{ padding: 0 }} onClick={handleSaveUsdRate}>保存</Button>
                            </>
                        ) : (
                            <>
                                <span style={{ color: '#606266', fontSize: 13 }}>$1.00按¥{usdRate.toFixed(2)}元折算</span>
                                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setUsdRateEditing(true)}>修改</Button>
                            </>
                        )}
                    </span>
                )}
                bordered={false}
                style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
            >
                <Row gutter={[16, 16]}>
                {kpiCards.slice(0, 5).map((c) => (
                    <Col flex="1 1 0" key={c.title}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', minHeight: 72 }}>
                            <div style={{ fontSize: 12, color: '#606266', marginBottom: 4 }}>
                                {c.title}
                                <Tooltip title={c.tip}><InfoCircleOutlined style={{ color: '#999', fontSize: 12, marginLeft: 4, cursor: 'help' }} /></Tooltip>
                            </div>
                            {c.profit ? (
                                <div style={{ color: c.value >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold', fontSize: 18, whiteSpace: 'nowrap', lineHeight: '24px' }}>
                                    {c.value >= 0 ? '+' : '-'}¥{Math.abs(c.value).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                                </div>
                            ) : c.multiCurrency ? (
                                <div>
                                    <div style={{ color: c.color, fontWeight: 'bold', fontSize: 15, whiteSpace: 'nowrap' }}>¥{c.cny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                    <div style={{ color: c.color, fontWeight: 'bold', fontSize: 15, whiteSpace: 'nowrap' }}>${c.usd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                </div>
                            ) : (
                                <div style={{ color: c.color, fontWeight: 'bold', fontSize: 18, whiteSpace: 'nowrap', lineHeight: '24px' }}>
                                    {c.money ? `¥${c.value.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}` : `${c.value}${c.suffix}`}
                                </div>
                            )}
                        </Card>
                    </Col>
                ))}
                </Row>
                <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                {kpiCards.slice(5, 10).map((c) => (
                    <Col flex="1 1 0" key={c.title}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', minHeight: 72 }}>
                            <div style={{ fontSize: 12, color: '#606266', marginBottom: 4 }}>
                                {c.title}
                                <Tooltip title={c.tip}><InfoCircleOutlined style={{ color: '#999', fontSize: 12, marginLeft: 4, cursor: 'help' }} /></Tooltip>
                            </div>
                            {c.profit ? (
                                <div style={{ color: c.value >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold', fontSize: 18, whiteSpace: 'nowrap', lineHeight: '24px' }}>
                                    {c.value >= 0 ? '+' : '-'}¥{Math.abs(c.value).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                                </div>
                            ) : c.multiCurrency ? (
                                <div>
                                    <div style={{ color: c.color, fontWeight: 'bold', fontSize: 15, whiteSpace: 'nowrap' }}>¥{c.cny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                    <div style={{ color: c.color, fontWeight: 'bold', fontSize: 15, whiteSpace: 'nowrap' }}>${c.usd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                </div>
                            ) : (
                                <div style={{ color: c.color, fontWeight: 'bold', fontSize: 18, whiteSpace: 'nowrap', lineHeight: '24px' }}>
                                    {c.money ? `¥${c.value.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}` : `${c.value}${c.suffix}`}
                                </div>
                            )}
                        </Card>
                    </Col>
                ))}
                </Row>
            </Card>

            {/* ═══════════ 趋势分析 ═══════════ */}
            <Card title="趋势分析" bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Row gutter={[16, 16]}>
                <Col span={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>设备状态趋势</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                            <Select value={timeRange} onChange={setTimeRange} options={timeRangeOptions} style={{ width: 120 }} />
                        </div>
                        <div ref={trend1Ref} style={{ width: '100%', height: 300 }} />
                </Col>
                <Col span={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>设备营收趋势</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                            <Select value={timeRange} onChange={setTimeRange} options={timeRangeOptions} style={{ width: 120 }} />
                        </div>
                        <div ref={trend2Ref} style={{ width: '100%', height: 300 }} />
                </Col>
                </Row>
            </Card>

            {/* ═══════════ 生产盈亏分析 ═══════════ */}
            <Card title="生产盈亏分析" bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Table columns={topColumns} dataSource={productList} rowKey="key" pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }} scroll={{ x: 2700 }} components={tableHeaderNowrap} style={{ whiteSpace: 'nowrap' }} />
            </Card>

            {/* ═══════════ 批次明细抽屉 ═══════════ */}
            <Drawer title={`批次明细 - ${batchRecord?.name || ''}`} width={1000} open={batchVisible} onClose={() => setBatchVisible(false)} destroyOnClose>
                {batchRecord && (
                    <>
                        <div style={{ background: '#f9f9f9', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Text type="secondary" style={{ display: 'inline-block', width: 88, textAlign: 'right', flexShrink: 0, marginRight: 8 }}>产品信息：</Text>
                                <div>
                                    <span style={{ fontWeight: 500 }}>{batchRecord.name}</span>
                                    <span style={{ margin: '0 8px', color: '#d9d9d9' }}>|</span>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{batchRecord.id}</Text>
                                </div>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ display: 'inline-block', width: 88, textAlign: 'right', marginRight: 8 }}>开发周期：</Text>
                                <Text strong>{batchRecord.devCycle}天</Text>
                            </div>
                            <div style={{ marginTop: 8, background: '#fff', padding: '8px 12px', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                                <div style={{ fontSize: 12, color: '#606266', lineHeight: '20px' }}>1. 生产周期：License生成（起始时间）——烧录完成（完结时间）的平均消耗时长</div>
                                <div style={{ fontSize: 12, color: '#606266', lineHeight: '20px' }}>2. 销售周期：License烧录完成（起始时间）——用户绑定设备（完结时间）的平均消耗时长</div>
                                <div style={{ fontSize: 12, color: '#606266', lineHeight: '20px' }}>3. 采购成本：License的采购总成本</div>
                                <div style={{ fontSize: 12, color: '#606266', lineHeight: '20px' }}>4. 批次营收：截止当前设备产生的增值服务总收入</div>
                            </div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <Input.Search placeholder="工单名称查询" allowClear onSearch={(v) => setBatchKeyword(v)} style={{ width: 240 }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            {batchDayRateEditing ? (
                                <>
                                    <span style={{ color: '#606266', fontSize: 13 }}>1天按¥</span>
                                    <InputNumber value={batchDayRateInput} onChange={setBatchDayRateInput} min={0} precision={2} size="small" style={{ width: 80 }} />
                                    <span style={{ color: '#606266', fontSize: 13 }}>折算</span>
                                    <Button type="link" size="small" style={{ padding: 0 }} onClick={handleSaveBatchDayRate}>保存</Button>
                                </>
                            ) : (
                                <>
                                    <span style={{ color: '#606266', fontSize: 13 }}>1天按¥{dayRate.toFixed(2)}元折算</span>
                                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => { setBatchDayRateInput(dayRate); setBatchDayRateEditing(true); }}>修改</Button>
                                </>
                            )}
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            {batchUsdRateEditing ? (
                                <>
                                    <span style={{ color: '#606266', fontSize: 13 }}>$1.00按¥</span>
                                    <InputNumber value={batchUsdRateInput} onChange={setBatchUsdRateInput} min={0} precision={2} size="small" style={{ width: 80 }} />
                                    <span style={{ color: '#606266', fontSize: 13 }}>折算</span>
                                    <Button type="link" size="small" style={{ padding: 0 }} onClick={handleSaveBatchUsdRate}>保存</Button>
                                </>
                            ) : (
                                <>
                                    <span style={{ color: '#606266', fontSize: 13 }}>$1.00按¥{usdRate.toFixed(2)}元折算</span>
                                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => { setBatchUsdRateInput(usdRate); setBatchUsdRateEditing(true); }}>修改</Button>
                                </>
                            )}
                        </div>
                        <Table size="small" columns={batchColumns} dataSource={filteredBatches} rowKey="key" scroll={{ x: 2400 }} pagination={{ pageSize: 5, showTotal: (t) => `共 ${t} 条` }} components={tableHeaderNowrap} />
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default DeviceDashboard;
